<script setup>
import { NDrawer } from 'naive-ui'
import { useI18n, useLocalePath, useSwitchLocalePath } from '#imports'
import { locales } from '@/utils/locale'
import { useUserStore } from '~/store/user'
import { useFooterLinks } from '../footer/config'
import { useComingSoonMessage } from '~/components/home/utils/message'
import DownloadVue from './icons/Download.vue'
import Language from './icons/Language.vue'
import iconClose from '@/assets/images/header/icon-close.svg'
import iconArrowRight from '@/assets/images/header/icon-arrow-right.svg'
import SocialIcons from '@/components/common/footer/SocialIcons.vue'

import DropDownUserHeader from './DropDownUserHeader.vue'

const props = defineProps({
  show: Boolean,
  menuList: {
    type: Array,
    default: () => [],
  },
})
const emit = defineEmits(['update:show'])
const { t, locale } = useI18n()
const localePath = useLocalePath()

const showComingSoonMessage = useComingSoonMessage()
const footerLinks = computed(() => useFooterLinks({ t, locale, localePath }))
const user = useUserStore()

const label = ref(locale.value)
const router = useRouter()

// 处理 locales 数据，转换为 n-select 需要的格式
const languageOptions = computed(() => {
  return locales.map(item => ({
    label: item.title,
    value: item.code,
  }))
})

function handleLocaleChange(code) {
  handleUpdateShow(false)
  const switchLocalePath = useSwitchLocalePath()
  const path = switchLocalePath(code) || `/${code}`
  router.push(path)
}

function handleUpdateShow(value) {
  emit('update:show', value)
}

function handleShowMessage(href) {
  if (!href) {
    return showComingSoonMessage()
  }
  emit('update:show', false)
}

const expandedMenu = ref(null)

function toggleExpand(index) {
  expandedMenu.value = expandedMenu.value === index ? null : index
}

function handleLoginOut() {
  user.logout()
  router.replace({
    path: localePath('/'),
  })
  emit('update:show', false)
}
</script>

<template>
  <NDrawer :show="show" placement="right" width="100%" class="bg-fillPagePrimary pt-6 !rounded-none" @update:show="handleUpdateShow">
    <div class="flex justify-end px-4">
      <component :is="iconClose" class="w-6 h-6 text-textPrimary" @click="handleUpdateShow(false)" />
    </div>

    <div class="flex flex-col pt-4">
      <div v-if="!user.isLogin" class="flex flex-col gap-4 mb-8 px-4 text-base">
        <NuxtLink
          :to="localePath('/log-in')"
          class="w-full h-11 flex items-center justify-center rounded-full border border-border bg-fillPagePrimary text-textPrimary"
          @click="handleUpdateShow(false)"
        >
          {{ $t('登录') }}
        </NuxtLink>
        <NuxtLink
          :to="localePath('/sign-up')"
          class="w-full h-11 flex items-center justify-center rounded-full bg-btnPrimary text-textOnBtn"
          @click="handleUpdateShow(false)"
        >
          {{ $t('注册') }}
        </NuxtLink>
      </div>
      <div v-else>
        <DropDownUserHeader class="pb-4" />
      </div>

      <div class="flex flex-col px-8">
        <!-- {{ menuList }} -->
        <NuxtLink
          v-for="(item, index) in props.menuList"
          :key="index"
          :to="localePath(item.href)"
          class="h-11 flex items-center text-sm text-textPrimary outline-none"
          @click="handleShowMessage(item.href)"
        >
          {{ item.label }}
        </NuxtLink>
      </div>

      <div class="flex flex-col gap-4">
        <div class="p-4 border-b border-t border-border space-y-2">
          <NuxtLink
            :to="localePath('/download')"
            class="h-10 flex items-center gap-2  text-textPrimary"
            @click="handleShowMessage('/download')"
          >
            <DownloadVue />
            <span>{{ $t('下载APP') }}</span>
          </NuxtLink>

          <div class="flex h-10 items-center justify-center gap-2">
            <Language class="w-5 h-5 ms-[3px] text-textPrimary flex-shrink-0" />
            <n-select
              v-model:value="label"
              :options="languageOptions"
              size="small"
              class="w-full"
              @update:value="handleLocaleChange"
            />
          </div>
        </div>

        <div class="flex flex-col gap-2 mt-2 ps-8 pe-4">
          <div v-for="(item, index) in footerLinks" :key="item.label" class="flex flex-col">
            <div
              class="h-10 flex items-center justify-between text-sm cursor-pointer text-textPrimary"
              @click="toggleExpand(index)"
            >
              {{ item.label }}
              <component :is="iconArrowRight" class="w-5 h-5 text-textPrimary rtl:-scale-x-100" :class="{ 'rotate-90': expandedMenu === index }" />
            </div>
            <div v-show="expandedMenu === index" class="flex flex-col ps-4  gap-3 pt-2">
              <NuxtLink
                v-for="(subItem) in item.list"
                :key="subItem.label"
                :to="subItem.href"
                class="h-6 flex items-center text-sm text-textSecondary"
                @click="handleShowMessage(subItem.href)"
              >
                {{ subItem.label }}
              </NuxtLink>
            </div>
          </div>
        </div>

        <SocialIcons class-name="p-4 border-b border-t border-border grid grid-cols-6 place-items-center" />
      </div>

      <div v-if="user.isLogin" class="flex flex-col gap-4 mt-8 mb-8 px-4">
        <common-x-button @click="handleLoginOut">
          {{ $t('退出登录') }}
        </common-x-button>
      </div>
    </div>
  </NDrawer>
</template>

<style scoped>
.rotate-90 {
  transform: rotate(90deg);
}
</style>
